import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container, Typography, TextField, Button, Paper, Stack, Box, Snackbar, Alert,
  IconButton, Grid, Avatar, Tooltip, MenuItem, useMediaQuery, Divider, Pagination,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import {
  Delete as DeleteIcon, Edit as EditIcon, Refresh as RefreshIcon,
  Logout as LogoutIcon, GridOn as GridOnIcon, Add as AddIcon,
  SupervisorAccount as AdminIcon
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const DEPT_URL = `${process.env.REACT_APP_API_URL}/api/tele/admin`;

// ===== WHITE THEME WITH YELLOW ACCENTS =====
const lightStyles = {
  paper: {
    bgcolor: "#ffffff",
    border: "1px solid #e0e0e0",
    color: "#000000",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      color: "#000000",
      backgroundColor: "#ffffff",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#FFD600" },
      "&.Mui-focused fieldset": { borderColor: "#FFD600" },
    },
    "& .MuiInputLabel-root": { color: "#666666" },
    "& .MuiSelect-icon": { color: "#FFD600" },
  },
  actionBtn: {
    bgcolor: "#FFD600",
    color: "#000000",
    fontWeight: "bold",
    "&:hover": {
      bgcolor: "#ffcc00",
      boxShadow: "0 4px 12px rgba(255, 214, 0, 0.5)",
    },
  },
  dialogPaper: {
    bgcolor: "#ffffff",
    border: "2px solid #FFD600",
    color: "#000000",
    backgroundImage: "none",
    borderRadius: "12px",
  },
  tableHeader: {
    bgcolor: "#f5f5f5",
    color: "#000000",
    fontWeight: "bold",
    borderBottom: "2px solid #FFD600",
  },
  tableCell: {
    color: "#000000",
    borderBottom: "1px solid #e0e0e0",
  }
};

const TeleSuperDashboard = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const token = localStorage.getItem("token");

  // State Management
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ name: "", username: "", password: "" });
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isAdminDialogOpen, setIsAdminDialogOpen] = useState(false);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  // API Calls
  const fetchAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${DEPT_URL}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdmins(Array.isArray(res.data.admins) ? res.data.admins : []);
    } catch (err) {
      showSnackbar("Sync failed", "error");
    }
  }, [token]);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Handlers
  const handleOpenCreate = () => {
    setEditId(null);
    setNewAdmin({ name: "", username: "", password: "" });
    setIsAdminDialogOpen(true);
  };

  const handleOpenEdit = (admin) => {
    setEditId(admin._id);
    setNewAdmin({ name: admin.name, username: admin.username, password: "" });
    setIsAdminDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editId) {
        await axios.put(`${DEPT_URL}/${editId}`, newAdmin, { headers: { Authorization: `Bearer ${token}` } });
        showSnackbar("Admin Updated");
      } else {
        await axios.post(`${DEPT_URL}/create`, newAdmin, { headers: { Authorization: `Bearer ${token}` } });
        showSnackbar("Admin Created");
      }
      setIsAdminDialogOpen(false);
      fetchAdmins();
    } catch (err) { showSnackbar("Save Failed", "error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this admin?")) return;
    try {
      await axios.delete(`${DEPT_URL}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      showSnackbar("Admin Deleted", "warning");
      fetchAdmins();
    } catch (err) { showSnackbar("Delete Failed", "error"); }
  };

  const downloadExcel = () => {
    const data = filteredAdmins.map(a => ({ Name: a.name, User: a.username, Role: a.role || "ADMIN" }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Admins");
    XLSX.writeFile(wb, `Admins_${new Date().toLocaleDateString()}.xlsx`);
    showSnackbar("Excel Exported", "success");
  };

  const filteredAdmins = useMemo(() => {
    return admins.filter(a =>
      a?.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      a?.username?.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [admins, debouncedSearch]);

  const paginatedAdmins = useMemo(() => {
    return filteredAdmins.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage);
  }, [filteredAdmins, page]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: "#f5f5f5", py: 4 }}>
      <Container maxWidth="lg">

        {/* HEADER */}
        <Paper sx={{ p: 2, mb: 4, ...lightStyles.paper, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: '#FFD600', color: '#000000', border: '1px solid #FFD600' }}>
              <AdminIcon />
            </Avatar>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight={900} sx={{ color: "#000000" }}>
              TELE
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Export Excel">
              <IconButton
                onClick={downloadExcel}
                sx={{ ...lightStyles.actionBtn, whiteSpace: 'nowrap' }}
              >
                <GridOnIcon />
              </IconButton>
            </Tooltip>
            {isMobile ? (
              <Tooltip title="Logout">
                <IconButton
                  onClick={() => { localStorage.removeItem("token"); navigate("/login"); }}
                  sx={{ color: "#ff0000", border: "1px solid #e0e0e0", bgcolor: "rgba(255, 0, 0, 0.05)", transition: "0.3s", "&:hover": { color: "#ff0000", border: "1px solid #ff4444", bgcolor: "rgba(255, 0, 0, 0.1)", boxShadow: "0 0 15px 2px rgba(255, 68, 68, 0.6)" } }}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                onClick={() => { localStorage.removeItem("token"); navigate("/login"); }}
                startIcon={<LogoutIcon />}
                sx={{ color: "#ff0000", border: "1px solid #e0e0e0", bgcolor: "rgba(255, 0, 0, 0.05)", transition: "0.3s", "&:hover": { color: "#ff0000", border: "1px solid #ff4444", bgcolor: "rgba(255, 0, 0, 0.1)", boxShadow: "0 0 15px 2px rgba(255, 68, 68, 0.6)" } }}
              >
                Logout
              </Button>
            )}
          </Stack>
        </Paper>

        {/* GROUPED ACTION TOOLBAR */}
        <Paper
          sx={{
            p: 1.5,
            mb: 3,
            ...lightStyles.paper,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "nowrap"
          }}
        >
          {/* Left Group */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              flexGrow: isMobile ? 1 : 0,
              minWidth: 0
            }}
          >
            {isMobile ? (
              <IconButton
                onClick={handleOpenCreate}
                sx={{
                  ...lightStyles.actionBtn,
                  borderRadius: 1,
                  width: 40,
                  height: 40
                }}
              >
                <AddIcon />
              </IconButton>
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
                sx={{ ...lightStyles.actionBtn, whiteSpace: 'nowrap' }}
              >
                New Admin
              </Button>
            )}

            <TextField
              placeholder={isMobile ? "Search..." : "Search database..."}
              size="small"
              sx={{
                ...lightStyles.input,
                width: isMobile ? 'auto' : '300px',
                flexGrow: isMobile ? 1 : 0,
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Typography variant="caption" sx={{ color: "#000000", fontWeight: "bold", border: "1px solid #e0e0e0", px: 1, py: 0.5, borderRadius: "4px", bgcolor: "rgba(255,214,0,0.05)", whiteSpace: "nowrap" }}>{admins.length}</Typography>
          </Stack>

          {/* Right Group */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexShrink: 0,
              alignItems: 'center'
            }}
          >
            <Tooltip title="Refresh Database">
              <IconButton
                onClick={fetchAdmins}
                sx={{ ...lightStyles.actionBtn, whiteSpace: 'nowrap' }}
              >
                <RefreshIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {/* TABLE */}
        <TableContainer component={Paper} sx={{ ...lightStyles.paper, maxHeight: '60vh', overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['NAME', 'USERNAME', 'ROLE', 'ACTIONS'].map(h => (
                  <TableCell key={h} sx={{ bgcolor: '#f5f5f5', color: '#000000', fontWeight: 'bold', borderBottom: '2px solid #FFD600' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAdmins.map((admin) => (
                <TableRow key={admin._id} hover sx={{ '&:hover': { bgcolor: 'rgba(255, 214, 0, 0.05)' } }}>
                  <TableCell sx={{ color: '#000000', fontWeight: 'bold' }}>{admin.name}</TableCell>
                  <TableCell sx={{ color: '#000000' }}>{admin.username}</TableCell>
                  <TableCell sx={{ color: admin.role === "TELE_SUPER" ? "#FFD600" : "#000000", fontWeight: 'bold' }}>
                    {admin.role || "ADMIN"}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(admin)}
                        sx={{ color: "#FFD600", "&:hover": { bgcolor: "#FFD600", color: "#000" } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(admin._id)}
                        sx={{ color: "#ff4444", "&:hover": { bgcolor: "#ff0000", color: "#fff" } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedAdmins.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ textAlign: 'center', color: '#666666', py: 4 }}>
                    No admins found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* PAGINATION */}
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(filteredAdmins.length / rowsPerPage)}
            page={page}
            onChange={(_, v) => setPage(v)}
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#000000",
                borderColor: "#e0e0e0",
                '&:hover': { bgcolor: 'rgba(255, 214, 0, 0.1)' }
              },
              "& .Mui-selected": {
                bgcolor: "#FFD600 !important",
                color: "#000000",
                '&:hover': { bgcolor: "#ffcc00 !important" }
              }
            }}
          />
        </Box>

        {/* POPUP DIALOG */}
        <Dialog
          open={isAdminDialogOpen}
          onClose={() => setIsAdminDialogOpen(false)}
          PaperProps={{ sx: lightStyles.dialogPaper }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ color: '#ffae00', borderBottom: '1px solid #e0e0e0', fontWeight: 'bold' }}>
            {editId ? "UPDATE ADMIN" : "NEW ADMIN ENTRY"}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Name"
                fullWidth
                sx={lightStyles.input}
                value={newAdmin.name}
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
              />
              <TextField
                label="Username"
                fullWidth
                sx={lightStyles.input}
                value={newAdmin.username}
                onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                sx={lightStyles.input}
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setIsAdminDialogOpen(false)} sx={{ color: "#666666" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={lightStyles.actionBtn}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert
            severity={snackbar.severity}
            variant="filled"
            sx={{ bgcolor: "#ffffff", color: "#000000", border: "1px solid #FFD600" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default TeleSuperDashboard;